import type { IScheduledEmailContentV2 } from '@/types/email.types';
import type { CampaignEditorReducerCreator } from '../types';
import type { RootState } from '@/store/main';
import { currentTemplateIdOf, editorBlocksFor, editorEventTitle, fieldsFromScheduledEmail } from '../helpers';

const loadCampaignEditor = (create: CampaignEditorReducerCreator) =>
  create.asyncThunk(
    async ({ campaignUuid, language }: { campaignUuid: string; language: string }, { getState }) => {
      const state = getState() as RootState;
      // the condition below guarantees the email is loaded
      const email = state.scheduledEmails.emails!.find((e) => e.uuid === campaignUuid)!;
      const event = state.project.workingProject;
      const baseLanguage = event?.settings.i18n?.base ?? 'en';
      const content = email.customizations.v2 as IScheduledEmailContentV2 | undefined;
      const templateId = currentTemplateIdOf(email);
      // blocks and templateId are always patched/cleared together server side see patchScheduledEmailCustomizations
      const blocks = editorBlocksFor(
        content?.blocks,
        templateId,
        editorEventTitle(event, language),
        language,
        baseLanguage
      );
      const fields = fieldsFromScheduledEmail(email, language);
      return { campaignUuid, language, baseLanguage, templateId, fields, blocks };
    },
    {
      options: {
        condition: ({ campaignUuid }, { getState }) =>
          !!(getState() as RootState).scheduledEmails.emails?.some((e) => e.uuid === campaignUuid),
      },
      fulfilled: (state, action) => {
        const { campaignUuid, language, baseLanguage, templateId, fields, blocks } = action.payload;
        state.campaignUuid = campaignUuid;
        state.language = language;
        state.baseLanguage = baseLanguage;
        state.templateId = templateId;
        state.fields = fields;
        state.blocks = blocks;
        state.initialized = true;
      },
    }
  );

export default loadCampaignEditor;
